import { useEffect, useRef, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Loader2, Plus, Bot, User, PlayCircle, XCircle, ExternalLink, Compass, Trash2, ImagePlus, X as XIcon, BookmarkPlus, Target, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

function OpsPanel({ channelId, navigate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const q = channelId ? `?channel_id=${channelId}` : "";
    api.get(`/command/ops-panel${q}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [channelId]);
  if (!data) return null;
  const cards = [
    { key: "running_tasks", label: "Running tasks", d: data.running_tasks, color: "text-sky-300", link: "/autopilot" },
    { key: "pending_approvals", label: "Pending approvals", d: data.pending_approvals, color: "text-amber-300", link: "/review" },
    { key: "ready_to_publish", label: "Ready to publish", d: data.ready_to_publish, color: "text-emerald-300", link: "/calendar" },
    { key: "failures", label: "Failures", d: data.failures, color: "text-rose-300", link: "/review" },
    { key: "language_attention", label: "Language attention", d: data.language_attention, color: "text-orange-300", link: "/pipeline" },
  ];
  return (
    <div className="mb-5" data-testid="ops-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wider text-slate-500">Operating center · scope: <span className="text-slate-300" data-testid="ops-scope">{data.scope}</span></span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map((c) => (
          <button key={c.key} onClick={() => navigate(c.link)} data-testid={`ops-${c.key}`}
            className="bg-[#111827] border border-slate-800/80 rounded-xl p-3 text-left hover:border-violet-500/40 transition-colors">
            <div className={`text-2xl font-display font-extrabold ${c.color}`}>{c.d.count}</div>
            <div className="text-xs text-slate-400 mt-0.5">{c.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function GoalsPanel({ navigate }) {
  const [goals, setGoals] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/goals"); setGoals(data); } catch (e) { logger.error("goals load failed", e); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const control = async (id, action) => {
    setBusy(id + action);
    try { await api.post(`/goals/${id}/controls/${action}`); toast.success(`Goal ${action}d`); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const runNow = async (id) => {
    setBusy(id + "run");
    try { const { data } = await api.post(`/goals/${id}/run-now`); toast.success(data.idempotent ? "Cycle already ran (no-op)" : `Cycle ${data.cycle} done`); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const active = goals.filter((g) => g.status !== "cancelled");
  if (active.length === 0) return null;
  return (
    <div className="mb-5" data-testid="goals-panel">
      <div className="flex items-center gap-2 mb-2">
        <Target className="w-3.5 h-3.5 text-violet-300" />
        <span className="text-[11px] uppercase tracking-wider text-slate-500">Standing goals (autonomous operator)</span>
      </div>
      <div className="space-y-3">
        {active.map((g) => {
          const pct = g.budget_cap ? Math.min(100, (g.cost_so_far / g.budget_cap) * 100) : 0;
          const reached = g.cost_so_far >= g.budget_cap;
          const log = (g.progress_log || []).slice(-4).reverse();
          return (
            <div key={g.id} className="bg-[#111827] border border-slate-800/80 rounded-xl p-4" data-testid={`goal-${g.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm text-slate-100 font-medium truncate">{g.goal}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">autonomy {g.autonomy} · every {g.cadence_days}d · {g.cycle_count} cycle(s) · <span className={g.status === "active" ? "text-emerald-400" : "text-amber-400"}>{g.status}</span></p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="outline" disabled={g.status !== "active" || busy === g.id + "run"} onClick={() => runNow(g.id)} className="h-7 border-slate-700 text-slate-300 hover:bg-slate-800" data-testid={`goal-run-${g.id}`}>{busy === g.id + "run" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Run now"}</Button>
                  {g.status === "active"
                    ? <Button size="sm" variant="outline" onClick={() => control(g.id, "pause")} className="h-7 border-slate-700 text-slate-300 hover:bg-slate-800" data-testid={`goal-pause-${g.id}`}>Pause</Button>
                    : <Button size="sm" variant="outline" onClick={() => control(g.id, "resume")} className="h-7 border-slate-700 text-slate-300 hover:bg-slate-800" data-testid={`goal-resume-${g.id}`}>Resume</Button>}
                  <Button size="sm" variant="ghost" onClick={() => control(g.id, "cancel")} className="h-7 text-rose-400 hover:bg-slate-800" data-testid={`goal-cancel-${g.id}`}>Cancel</Button>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className={`h-full rounded-full ${reached ? "bg-rose-500" : "bg-violet-500"}`} style={{ width: `${pct}%` }} /></div>
                <span className={`text-[11px] ${reached ? "text-rose-400" : "text-slate-400"}`} data-testid={`goal-budget-${g.id}`}>${(g.cost_so_far || 0).toFixed(3)} / ${g.budget_cap}{reached ? " · cap reached" : ""}</span>
              </div>
              {log.length > 0 && (
                <div className="mt-2 space-y-1" data-testid={`goal-feed-${g.id}`}>
                  {log.map((a, i) => (
                    <div key={i} className="text-[11px] text-slate-400 flex items-start gap-1.5">
                      <span className={a.status === "ok" ? "text-emerald-400" : a.status === "awaiting" || a.status === "blocked" ? "text-amber-400" : a.status === "budget_reached" || a.status === "failed" ? "text-rose-400" : "text-slate-500"}>●</span>
                      <span className="flex-1 min-w-0 break-words [overflow-wrap:anywhere]"><b className="text-slate-300">{a.action}</b>: {a.detail}</span>
                      {a.link && <button onClick={() => navigate(a.link)} className="text-violet-300 hover:text-violet-200 flex-none"><ExternalLink className="w-3 h-3" /></button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function CommandCenter() {
  const { channels, selectedChannelId } = useStudio();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  const [contextMode, setContextMode] = useState("auto"); // auto | workspace | channel
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]); // {id,url,content_type,filename}
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // conv id | "all"
  const [brandAtt, setBrandAtt] = useState(null); // attachment to save as brand asset
  const [brandChannel, setBrandChannel] = useState("");
  const fileRef = useRef();
  const endRef = useRef();

  const loadConvs = useCallback(async () => {
    const { data } = await api.get("/command/conversations");
    setConversations(data);
  }, []);

  const loadProjects = useCallback(async () => {
    if (!selectedChannelId) { setProjects([]); return; }
    const { data } = await api.get(`/projects?channel_id=${selectedChannelId}`);
    setProjects(data);
  }, [selectedChannelId]);

  // Workspace-level: load ALL conversations once. Switching the top-bar channel
  // only refreshes the project picker — it NEVER resets the chat or history.
  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openConv = async (id) => {
    setConvId(id);
    const { data } = await api.get(`/command/conversations/${id}/messages`);
    setMessages(data);
  };

  const send = async () => {
    if (!input.trim() && attachments.length === 0) return;
    const text = input.trim();
    const atts = attachments;
    setInput("");
    setAttachments([]);
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", text, attachments: atts }]);
    setBusy(true);
    try {
      const { data } = await api.post("/command/message", {
        conversation_id: convId,
        channel_id: contextMode === "workspace" ? null : (selectedChannelId || null),
        project_id: contextMode === "workspace" ? null : (projectId || null),
        context_mode: contextMode, message: text,
        attachments: atts.map((a) => a.id),
      });
      if (!convId) { setConvId(data.conversation_id); loadConvs(); }
      setMessages((m) => [...m.filter((x) => !String(x.id).startsWith("tmp-")), { role: "user", text, attachments: atts, id: `u-${Date.now()}` }, ...data.messages]);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const pickImage = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/command/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, data]);
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };
  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const saveBrandAsset = async () => {
    if (!brandAtt || !brandChannel) return;
    try {
      await api.post(`/channels/${brandChannel}/brand-assets`, { file_id: brandAtt.id });
      const ch = channels.find((c) => c.id === brandChannel);
      toast.success(`Saved to ${ch?.data?.name || "channel"} brand assets`);
      setBrandAtt(null); setBrandChannel("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteConv = async (id) => {
    try {
      await api.delete(`/command/conversations/${id}`);
      if (convId === id) { setConvId(null); setMessages([]); }
      toast.success("Conversation deleted");
      loadConvs();
    } catch (e) { toast.error(apiError(e)); }
    finally { setConfirmDelete(null); }
  };
  const clearAll = async () => {
    try {
      await api.delete("/command/conversations");
      setConvId(null); setMessages([]);
      toast.success("All conversations cleared");
      loadConvs();
    } catch (e) { toast.error(apiError(e)); }
    finally { setConfirmDelete(null); }
  };

  const runPlan = async (planId) => {
    setBusy(true);
    try { const { data } = await api.post(`/command/plans/${planId}/approve`); setMessages((m) => [...m, data.message]); loadProjects(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const cancelPlan = async (planId) => {
    try { const { data } = await api.post(`/command/plans/${planId}/cancel`); setMessages((m) => [...m, data.message]); }
    catch (e) { toast.error(apiError(e)); }
  };

  const channelName = channels.find((c) => c.id === selectedChannelId)?.data?.name || "All channels";

  return (
    <div className="animate-in-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1">Foundation</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">AI Command Center</h1>
          <p className="text-sm text-slate-400 mt-2">Workspace-level chat — your history persists across channel switches.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Context</span>
          <Select value={contextMode} onValueChange={setContextMode}>
            <SelectTrigger className="h-9 w-[220px] bg-slate-900 border-slate-700 text-slate-200 text-sm" data-testid="context-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
              <SelectItem value="auto" data-testid="context-mode-auto">Auto{selectedChannelId ? ` · ${channelName}` : ""}</SelectItem>
              <SelectItem value="workspace" data-testid="context-mode-workspace">Entire workspace</SelectItem>
              <SelectItem value="channel" data-testid="context-mode-channel" disabled={!selectedChannelId}>Specific channel{selectedChannelId ? ` · ${channelName}` : ""}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <OpsPanel channelId={contextMode === "channel" ? selectedChannelId : null} navigate={navigate} />

      <GoalsPanel navigate={navigate} />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5" style={{ height: "calc(100vh - 230px)" }}>
        <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-3 flex flex-col">
          <Button onClick={() => { setConvId(null); setMessages([]); }} size="sm" className="bg-violet-600 hover:bg-violet-500 text-white mb-2" data-testid="new-conversation"><Plus className="w-4 h-4 mr-1.5" /> New chat</Button>
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[11px] uppercase tracking-wider text-slate-600">History</span>
            {conversations.length > 0 && (
              <button onClick={() => setConfirmDelete("all")} className="text-[11px] text-slate-500 hover:text-red-400 flex items-center gap-1" data-testid="clear-all-conversations"><Trash2 className="w-3 h-3" /> Clear all</button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto space-y-1" data-testid="conversation-list">
            {conversations.length === 0 ? <p className="text-xs text-slate-600 px-2">No conversations yet.</p> :
              conversations.map((c) => (
                <div key={c.id} className={`group flex items-center gap-1 rounded-lg ${convId === c.id ? "bg-violet-600/15" : "hover:bg-slate-800/50"}`} data-testid={`conversation-item-${c.id}`}>
                  <button onClick={() => openConv(c.id)} className={`flex-1 min-w-0 text-left px-3 py-2 rounded-lg text-sm truncate ${convId === c.id ? "text-violet-200" : "text-slate-400"}`}>
                    {c.title}
                  </button>
                  <button onClick={() => setConfirmDelete(c.id)} className="flex-none p-1.5 mr-1 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-conversation-${c.id}`} title="Delete conversation"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-[#111827] border border-slate-800/80 rounded-xl flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="message-thread">
            {messages.length === 0 ? (
              <EmptyState icon={Compass} title="Start a command" description="Try “list all my channels”, “earnings for all channels this month”, or “create a channel with AI about budget travel”." />
            ) : messages.map((m, i) => {
              const isSystemAlert = m.role === "system-alert" || m.type === "stale-alert";
              return (
              <div key={m.id || i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`msg-${isSystemAlert ? "system-alert" : m.role}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-none ${isSystemAlert ? "bg-amber-500/20" : m.role === "user" ? "bg-slate-700" : "bg-violet-600/80"}`}>
                  {isSystemAlert ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : m.role === "user" ? <User className="w-4 h-4 text-slate-200" /> : <Bot className="w-4 h-4 text-white" />}
                </div>
                <div className={`min-w-0 max-w-[80%] rounded-xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${isSystemAlert ? "bg-amber-500/10 border border-amber-500/30 text-amber-100" : m.role === "user" ? "bg-slate-800 text-slate-100" : "bg-slate-900/70 border border-slate-800 text-slate-200"}`}>
                  {m.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2" data-testid={`msg-attachments-${m.id || i}`}>
                      {m.attachments.map((a) => (
                        <div key={a.id} className="w-28">
                          <a href={`${API}${a.url}`} target="_blank" rel="noreferrer" data-testid={`attachment-thumb-${a.id}`}>
                            <img src={`${API}${a.url}`} alt={a.filename || "attachment"} className="w-28 h-28 object-cover rounded-lg border border-slate-700 hover:border-violet-400 transition-colors" />
                          </a>
                          <button onClick={() => { setBrandAtt(a); setBrandChannel(channels[0]?.id || ""); }} className="mt-1 w-full flex items-center justify-center gap-1 text-[10px] text-violet-300 hover:text-violet-200" data-testid={`save-brand-${a.id}`}><BookmarkPlus className="w-3 h-3" /> Save as brand asset</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {m.text}
                  {m.kind === "plan" && m.plan_id && (
                    <div className="mt-3 flex items-center gap-2" data-testid={`plan-actions-${m.plan_id}`}>
                      <Button size="sm" onClick={() => runPlan(m.plan_id)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`run-plan-${m.plan_id}`}><PlayCircle className="w-4 h-4 mr-1.5" /> Run</Button>
                      <Button size="sm" variant="outline" onClick={() => cancelPlan(m.plan_id)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid={`cancel-plan-${m.plan_id}`}><XCircle className="w-4 h-4 mr-1.5" /> Cancel</Button>
                    </div>
                  )}
                  {m.link && (
                    <button onClick={() => navigate(m.link)} className="mt-2 flex items-center gap-1 text-xs text-violet-300 hover:underline" data-testid="msg-link"><ExternalLink className="w-3 h-3" /> Open</button>
                  )}
                </div>
              </div>
            )})}
            {busy && <div className="flex gap-3"><div className="w-8 h-8 rounded-lg bg-violet-600/80 flex items-center justify-center"><Bot className="w-4 h-4 text-white" /></div><div className="rounded-xl px-4 py-2.5 bg-slate-900/70 border border-slate-800"><Loader2 className="w-4 h-4 animate-spin text-violet-300" /></div></div>}
            <div ref={endRef} />
          </div>

          <div className="border-t border-slate-800 p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-slate-500">Project context:</span>
              <Select value={projectId || "none"} onValueChange={(v) => setProjectId(v === "none" ? "" : v)}>
                <SelectTrigger className="h-7 w-56 bg-slate-900 border-slate-800 text-slate-200 text-xs" data-testid="context-project"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                  <SelectItem value="none">None</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="attachment-tray">
                {attachments.map((a) => (
                  <div key={a.id} className="relative group" data-testid={`pending-attachment-${a.id}`}>
                    <img src={`${API}${a.url}`} alt={a.filename} className="w-16 h-16 object-cover rounded-lg border border-slate-700" />
                    <button onClick={() => removeAttachment(a.id)} className="absolute -top-1.5 -right-1.5 bg-slate-800 border border-slate-600 rounded-full p-0.5 text-slate-300 hover:text-red-400" data-testid={`remove-attachment-${a.id}`}><XIcon className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="attachment-input" />
            <div className="flex items-end gap-2">
              <Button onClick={pickImage} disabled={uploading} variant="outline" size="icon" className="h-[46px] w-[46px] flex-none border-slate-800 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-violet-300" data-testid="attach-image-button" title="Attach image">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
              </Button>
              <Textarea value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type a command…" data-testid="command-input"
                className="bg-slate-900 border-slate-800 text-slate-100 min-h-[46px] max-h-32 focus-visible:ring-violet-500/30" />
              <Button onClick={send} disabled={busy || (!input.trim() && attachments.length === 0)} className="bg-violet-600 hover:bg-violet-500 text-white h-[46px]" data-testid="command-send">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#111827] border-slate-700 text-slate-100" data-testid="delete-conversation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDelete === "all" ? "Clear all conversations?" : "Delete this conversation?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This permanently deletes {confirmDelete === "all" ? "every conversation" : "the conversation"} and all its messages. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700" data-testid="cancel-delete-conversation">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDelete === "all" ? clearAll() : deleteConv(confirmDelete)} className="bg-red-600 hover:bg-red-500 text-white" data-testid="confirm-delete-conversation">
              {confirmDelete === "all" ? "Clear all" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!brandAtt} onOpenChange={(o) => !o && setBrandAtt(null)}>
        <DialogContent className="bg-[#111827] border-slate-700 text-slate-100" data-testid="save-brand-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookmarkPlus className="w-5 h-5 text-violet-300" /> Save as brand asset</DialogTitle>
            <DialogDescription className="text-slate-400">Copy this image into a channel's brand visual references. It then guides that channel's script/storyboard/autopilot generation.</DialogDescription>
          </DialogHeader>
          {channels.length === 0 ? (
            <p className="text-sm text-amber-300" data-testid="brand-no-channels">Create a channel first — there's nowhere to save this yet.</p>
          ) : (
            <div className="py-1">
              <label className="text-sm text-slate-300 mb-1.5 block">Target channel</label>
              <Select value={brandChannel} onValueChange={setBrandChannel}>
                <SelectTrigger className="bg-slate-900 border-slate-800 text-slate-200" data-testid="brand-channel-select"><SelectValue placeholder="Pick a channel" /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                  {channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.data?.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBrandAtt(null)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="cancel-save-brand">Cancel</Button>
            <Button onClick={saveBrandAsset} disabled={!brandChannel || channels.length === 0} className="bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-40" data-testid="confirm-save-brand">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
